import vendorService, { useVendorCategories, UpdateVendorRequest } from "@/services/vendorService";
import { useGetNetSuiteTaxDropdowns, useGetNetSuiteStates, useGetNetSuiteReferenceList } from "@/services/netsuiteService";
import { NETSUITE_COUNTRIES } from "@/utils/netsuiteCountries";
import { useUserStore } from "@/stores/userStore";
import { gstNumberValidator, panNumberValidator, phoneValidator, pinItemProps, urlValidator } from "@/utils/validation";
import { ALLOWED_COUNTRIES, COUNTRY_BANK_FIELDS, COUNTRY_TIMEZONES, AllowedCountry } from "@/utils/countryConstants";
import { Button, Form, Input, Modal, Radio, Select, Spin, message } from "antd";
import type { UploadFile } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import DuplicateSelectionModal from "@/components/shared/DuplicateSelectionModal";
import ClientDocumentUploadField from "@/components/shared/ClientDocumentUploadField";
import { handleFormValidationFailed } from "@/utils/validation";
import { openDocumentFromApi } from "@/utils/openDocument";
import CustomDocumentUploadList, { CustomDocumentEntry, getRemovedCustomDocumentIds } from "@/components/shared/CustomDocumentUploadList";
import { VendorType } from "@/interfaces";
import { CreateVendorRequest } from "@/services/vendorService";

const OTHER_CATEGORY = "__other__";

type VendorDocumentKey =
  | "panCard"
  | "gstCertificate"
  | "cancelledCheque"
  | "msmeCertificate"
  | "w9"
  | "voidedCheque"
  | "certificateOfIncorporation"
  | "smallBusinessCert";

interface VendorDocumentSpec {
  key: VendorDocumentKey;
  fieldName: string;
  label: string;
  required: boolean;
  numberField?: { name: "panNumber" | "gstNumber"; label: string; validator: typeof panNumberValidator };
}

const INDIA_DOCUMENTS: VendorDocumentSpec[] = [
  {
    key: "panCard",
    fieldName: "panCardFile",
    label: "PAN Card",
    required: true,
    numberField: { name: "panNumber", label: "PAN Number", validator: panNumberValidator },
  },
  {
    key: "gstCertificate",
    fieldName: "gstCertificateFile",
    label: "GST Certificate",
    required: true,
    numberField: { name: "gstNumber", label: "GST Number", validator: gstNumberValidator },
  },
  { key: "cancelledCheque", fieldName: "cancelledChequeFile", label: "Cancelled Cheque", required: true },
  { key: "msmeCertificate", fieldName: "msmeCertificateFile", label: "MSME Certificate (if applicable)", required: false },
];

const US_DOCUMENTS: VendorDocumentSpec[] = [
  { key: "w9", fieldName: "w9File", label: "W-9", required: true },
  { key: "voidedCheque", fieldName: "voidedChequeFile", label: "Voided Cheque", required: true },
  { key: "certificateOfIncorporation", fieldName: "certificateOfIncorporationFile", label: "Certificate of Incorporation", required: true },
  { key: "smallBusinessCert", fieldName: "smallBusinessCertFile", label: "Small Business Certification (if applicable)", required: false },
];

const EMPTY_DOCUMENT_FILES: Record<VendorDocumentKey, UploadFile[]> = {
  panCard: [],
  gstCertificate: [],
  cancelledCheque: [],
  msmeCertificate: [],
  w9: [],
  voidedCheque: [],
  certificateOfIncorporation: [],
  smallBusinessCert: [],
};

const getDocumentCountryMode = (country?: string | null): "india" | "us" | "other" => {
  if (country === "United States") return "us";
  if (!country || country === "India") return "india";
  return "other";
};

interface RequestVendorModalProps {
  open: boolean;
  vendor?: Partial<VendorType>;
  onCancel: () => void;
  onSuccess: () => void;
}

interface RequestVendorFormValues {
  vendorName: string;
  vendorLegalName: string;
  type: "individual" | "company";
  title?: string;
  firstName?: string;
  lastName?: string;
  domain: string;
  email?: string;
  phone?: string;
  teamsId?: string;
  skypeId?: string;
  linkedinUrl?: string;
  otherIntegrationId?: string;
  category: string;
  customCategory?: string;
  currency?: string;
  paymentTerms?: string;
  country?: string;
  timezone?: string;
  description?: string;
  address?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  pin?: string;
  panNumber?: string;
  panCardFile?: string;
  gstNumber?: string;
  gstCertificateFile?: string;
  cancelledChequeFile?: string;
  msmeCertificateFile?: string;
  w9File?: string;
  voidedChequeFile?: string;
  certificateOfIncorporationFile?: string;
  smallBusinessCertFile?: string;
  customDocuments?: unknown;
  bankName?: string;
  accountNumber?: string;
  accountHolderName?: string;
  ifscCode?: string;
  accountType?: "Savings" | "Current";
  bankMetadata?: Record<string, string>;
  bankDetailNotes?: string;
}

type RequestVendorPayload = Omit<RequestVendorFormValues, "bankMetadata"> & {
  bankMetadata?: string;
};

/**
 * Admin-only vendor create/edit modal.
 * Creates a new vendor, or edits an existing one when `vendor` is provided —
 * both flows share the exact same form so Admin's create and edit experiences match.
 */
export default function RequestVendorModal({ open, vendor, onCancel, onSuccess }: RequestVendorModalProps) {
  const [form] = Form.useForm<RequestVendorFormValues>();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [foundDuplicates, setFoundDuplicates] = useState<VendorType[]>([]);
  const [pendingValues, setPendingValues] = useState<RequestVendorPayload | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [documentFiles, setDocumentFiles] = useState<Record<VendorDocumentKey, UploadFile[]>>(EMPTY_DOCUMENT_FILES);
  const [customDocuments, setCustomDocuments] = useState<CustomDocumentEntry[]>([]);
  const [isOtherCategory, setIsOtherCategory] = useState(false);
  const [isIndividual, setIsIndividual] = useState(false);
  const documentMode = getDocumentCountryMode(selectedCountry);
  const currentCompany = useUserStore((state) => state.currentCompany);
  const queryClient = useQueryClient();
  const { data: categoriesData, isLoading: isCategoriesLoading } = useVendorCategories();
  const categoryOptions = [
    ...(categoriesData?.data || []).map((name) => ({ label: name, value: name })),
    { label: "Other", value: OTHER_CATEGORY },
  ];
  const { data: netsuiteDropdowns, isLoading: isCurrencyLoading } = useGetNetSuiteTaxDropdowns(currentCompany?.id);
  const isNetSuiteCompany = currentCompany?.invoicingAccounting === "NetSuite";
  const netsuiteCountryCode = NETSUITE_COUNTRIES.find((c) => c.name === selectedCountry)?.code;
  const { data: netsuiteStates, isLoading: isStatesLoading } = useGetNetSuiteStates(currentCompany?.id, netsuiteCountryCode);
  const { data: paymentTermsData, isLoading: isPaymentTermsLoading } = useGetNetSuiteReferenceList(
    "Terms",
    currentCompany?.id,
    isNetSuiteCompany,
  );
  const { data: industryListData, isLoading: isIndustryLoading } = useGetNetSuiteReferenceList(
    "Industry",
    currentCompany?.id,
    isNetSuiteCompany,
  );
  const isEditMode = !!vendor?.id;

  useEffect(() => {
    if (open && vendor) {
      form.setFieldsValue({
        vendorName: vendor.vendorName,
        vendorLegalName: vendor.vendorLegalName,
        type: vendor.type || "company",
        title: vendor.title || undefined,
        firstName: vendor.firstName || "",
        lastName: vendor.lastName || "",
        domain: vendor.domain,
        email: vendor.email || "",
        phone: vendor.phone || "",
        linkedinUrl: vendor.linkedinUrl || "",
        country: vendor.country || undefined,
        timezone: vendor.timezone || undefined,
        address: vendor.address || "",
        addressLine1: vendor.addressLine1 || "",
        addressLine2: vendor.addressLine2 || "",
        city: vendor.city || "",
        state: vendor.state || undefined,
        pin: vendor.pin || "",
        panNumber: vendor.panNumber || "",
        gstNumber: vendor.gstNumber || "",
        paymentTerms: vendor.paymentTerms || undefined,
        category: vendor.category,
        currency: vendor.currency || undefined,
        description: vendor.description || "",
        bankName: vendor.bankName || "",
        accountNumber: vendor.accountNumber || "",
        accountHolderName: vendor.accountHolderName || "",
        ifscCode: vendor.ifscCode || "",
        accountType: vendor.accountType || undefined,
        bankMetadata: vendor.bankMetadata || {},
        bankDetailNotes: vendor.bankDetailNotes || "",
      });
      setSelectedCountry(vendor.country || null);
      setIsIndividual(vendor.type === "individual");

      // Restore already-uploaded fixed documents so editing doesn't wipe them
      // out or block save behind the "required" validation.
      const newDocumentFiles: Record<VendorDocumentKey, UploadFile[]> = { ...EMPTY_DOCUMENT_FILES };
      [...INDIA_DOCUMENTS, ...US_DOCUMENTS].forEach((doc) => {
        const fileVal = (vendor as any)[doc.fieldName];
        const fileNameVal = (vendor as any)[`${doc.key}FileName`];
        if (fileVal) {
          newDocumentFiles[doc.key] = [
            { uid: `-existing-${doc.key}`, name: fileNameVal || "Existing File", status: "done" } as UploadFile,
          ];
          form.setFieldsValue({ [doc.fieldName]: fileNameVal || "Existing File" });
        }
      });
      setDocumentFiles(newDocumentFiles);

      // Restore existing custom documents (non-India/US countries)
      setCustomDocuments(
        (vendor.customDocuments || []).map((doc) => ({
          key: doc.id,
          name: doc.name,
          file: [{ uid: `-existing-${doc.id}`, name: doc.fileName || doc.name, status: "done" } as UploadFile],
        })),
      );

      setIsOtherCategory(false);
    } else if (!open) {
      form.resetFields();
      setSelectedCountry(null);
      setDocumentFiles(EMPTY_DOCUMENT_FILES);
      setCustomDocuments([]);
      setIsOtherCategory(false);
      setIsIndividual(false);
    }
  }, [open, vendor, form]);

  const handleCancel = () => {
    form.resetFields();
    setSelectedCountry(null);
    setDocumentFiles(EMPTY_DOCUMENT_FILES);
    setCustomDocuments([]);
    setIsOtherCategory(false);
    setIsIndividual(false);
    setPendingValues(null);
    setShowDuplicateModal(false);
    onCancel();
  };

  const uploadExtraDocuments = async (vendorId: string) => {
    const activeDocuments = documentMode === "us" ? US_DOCUMENTS : INDIA_DOCUMENTS;
    for (const doc of activeDocuments) {
      const docFile = documentFiles[doc.key]?.[0]?.originFileObj;
      if (!docFile) continue;

      try {
        const formData = new FormData();
        formData.append("document", docFile as File);
        await vendorService.uploadVendorDocument(vendorId, doc.key, formData);
      } catch (error: unknown) {
        console.error(`Failed to upload ${doc.label}:`, error);
        message.warning(`Vendor created but ${doc.label} upload failed. Please try uploading again.`);
      }
    }

    if (documentMode === "other") {
      for (const doc of customDocuments) {
        const docFile = doc.file[0]?.originFileObj;
        if (!docFile || !doc.name.trim()) continue;

        try {
          const formData = new FormData();
          formData.append("document", docFile as File);
          formData.append("name", doc.name.trim());
          await vendorService.uploadVendorCustomDocument(vendorId, formData);
          if (vendor?.customDocuments?.some((d) => d.id === doc.key)) {
            await vendorService.deleteVendorCustomDocument(vendorId, doc.key);
          }
        } catch (error: unknown) {
          console.error(`Failed to upload ${doc.name}:`, error);
          message.warning(`Vendor created but "${doc.name}" upload failed. Please try uploading again.`);
        }
      }
      for (const docId of getRemovedCustomDocumentIds(vendor?.customDocuments, customDocuments)) {
        try {
          await vendorService.deleteVendorCustomDocument(vendorId, docId);
        } catch (error: unknown) {
          console.error("Failed to remove document:", error);
          message.warning("Saved, but a removed document could not be deleted. Please try again.");
        }
      }
    }
  };

  const proceedWithCreation = async (values: RequestVendorPayload) => {
    if (!currentCompany?.id) return;
    setIsSaving(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
        panCardFile: _panCardFile,
        gstCertificateFile: _gstCertificateFile,
        cancelledChequeFile: _cancelledChequeFile,
        msmeCertificateFile: _msmeCertificateFile,
        w9File: _w9File,
        voidedChequeFile: _voidedChequeFile,
        certificateOfIncorporationFile: _certificateOfIncorporationFile,
        smallBusinessCertFile: _smallBusinessCertFile,
        customDocuments: _customDocumentsField,
        customCategory: _customCategory,
        ...createValues
      } = values;

      const requestPayload: CreateVendorRequest = {
        ...createValues,
        vendorType: "purchase",
        companies: [currentCompany.id],
        isActive: true,
      };

      const response = await vendorService.create(requestPayload);

      message.success(response.message || "Vendor created successfully");
      if (response.data?.id) {
        await uploadExtraDocuments(response.data.id);
      }
      queryClient.invalidateQueries({ queryKey: ["vendors", "categories"] });
      form.resetFields();
      setSelectedCountry(null);
      setDocumentFiles(EMPTY_DOCUMENT_FILES);
      setIsOtherCategory(false);
      setPendingValues(null);
      setShowDuplicateModal(false);
      onSuccess();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || "Failed to create vendor");
    } finally {
      setIsSaving(false);
    }
  };

  const proceedWithUpdate = async (values: RequestVendorPayload) => {
    if (!vendor?.id) return;
    setIsSaving(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
        panCardFile: _panCardFile,
        gstCertificateFile: _gstCertificateFile,
        cancelledChequeFile: _cancelledChequeFile,
        msmeCertificateFile: _msmeCertificateFile,
        w9File: _w9File,
        voidedChequeFile: _voidedChequeFile,
        certificateOfIncorporationFile: _certificateOfIncorporationFile,
        smallBusinessCertFile: _smallBusinessCertFile,
        customDocuments: _customDocumentsField,
        customCategory: _customCategory,
        ...updateValues
      } = values;

      const requestPayload: UpdateVendorRequest = {
        ...updateValues,
        vendorType: vendor.vendorType || "purchase",
      };

      const response = await vendorService.update(vendor.id, requestPayload);

      message.success(response.message || "Vendor updated successfully");
      await uploadExtraDocuments(vendor.id);
      queryClient.invalidateQueries({ queryKey: ["vendors", "categories"] });
      form.resetFields();
      setSelectedCountry(null);
      setDocumentFiles(EMPTY_DOCUMENT_FILES);
      setIsOtherCategory(false);
      onSuccess();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || "Failed to update vendor");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (values: RequestVendorFormValues) => {
    let finalBankMetadata: Record<string, string> = {};
    const cleanedValues = Object.fromEntries(Object.entries(values).filter(([_, v]) => v !== "")) as RequestVendorFormValues;

    if (cleanedValues.category === OTHER_CATEGORY) {
      cleanedValues.category = (cleanedValues.customCategory || "").trim();
    }

    if (cleanedValues.country && COUNTRY_BANK_FIELDS[cleanedValues.country as AllowedCountry]) {
      finalBankMetadata = {};
      (COUNTRY_BANK_FIELDS[cleanedValues.country as AllowedCountry] || []).forEach((field) => {
        const val = values.bankMetadata?.[field.key];
        if (val !== undefined && val !== "") {
          finalBankMetadata[field.key] = val;
        }
      });
    }

    // Vendor Type / title / name / Currency / structured address are NetSuite-only features —
    // when the current company isn't NetSuite, those Form.Items aren't rendered, so force the
    // sane "company" default so the backend's conditional validation behaves like pre-session.
    if (!isNetSuiteCompany) {
      cleanedValues.type = "company";
    } else {
      const stateLabel = (netsuiteStates || []).find((s) => (s.shortname || s.name) === values.state)?.name;
      cleanedValues.address = [
        values.addressLine1,
        values.addressLine2,
        values.city,
        stateLabel,
        values.pin,
        values.country,
      ]
        .filter(Boolean)
        .join(", ");
    }

    const payload: RequestVendorPayload = {
      ...cleanedValues,
      bankMetadata: JSON.stringify(finalBankMetadata),
    };

    if (isEditMode) {
      await proceedWithUpdate(payload);
      return;
    }

    if (!currentCompany?.id) {
      message.error("No company selected");
      return;
    }

    try {
      setIsSaving(true);
      const dupResp = await vendorService.checkDuplicates(values.vendorName, values.domain);
      const duplicates = dupResp?.data || [];
      const untaggedDuplicates = duplicates.filter(
        (d) => !d.companies?.some((c) => c.id === currentCompany.id),
      );

      if (untaggedDuplicates.length > 0) {
        setFoundDuplicates(untaggedDuplicates);
        setPendingValues(payload);
        setShowDuplicateModal(true);
        setIsSaving(false);
        return;
      }

      // If there are duplicates but all are already tagged to this company
      if (duplicates.length > 0) {
        message.warning(`A vendor with this name or domain already exists for your company.`);
        setIsSaving(false);
        return;
      }

      await proceedWithCreation(payload);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || "Failed to check duplicates");
      setIsSaving(false);
    }
  };

  const handleSelectDuplicate = async (item: VendorType | any) => {
    if (!currentCompany?.id) return;
    setIsSaving(true);
    try {
      const result = await vendorService.mapToCompany(item.id, currentCompany.id);
      message.success(result.message || "Existing vendor associated with your company successfully!");
      setShowDuplicateModal(false);
      setPendingValues(null);
      onSuccess();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || "Failed to associate existing vendor");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={<span className="text-2xl font-bold">{isEditMode ? "Edit Vendor" : "Create New Vendor"}</span>}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit} onFinishFailed={handleFormValidationFailed}
        initialValues={{ type: "company" }}
      >
        {isNetSuiteCompany && (
          <>
            <Form.Item name="type" label="Vendor Type" rules={[{ required: true }]}>
              <Radio.Group
                onChange={(e) => {
                  const individual = e.target.value === "individual";
                  setIsIndividual(individual);
                  form.setFieldsValue({ title: undefined, firstName: undefined, lastName: undefined });
                }}
              >
                <Radio.Button value="company">Company</Radio.Button>
                <Radio.Button value="individual">Individual</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {isIndividual && (
              <>
                <Form.Item name="title" label="Title" rules={[{ required: true, message: "Title is required" }]}>
                  <Select
                    placeholder="Select title"
                    options={[
                      { label: "Mr", value: "Mr" },
                      { label: "Mrs", value: "Mrs" },
                      { label: "Ms", value: "Ms" },
                    ]}
                  />
                </Form.Item>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="firstName"
                    label="First Name"
                    className="mb-0"
                    rules={[{ required: true, message: "First name is required" }]}
                  >
                    <Input placeholder="Enter first name" />
                  </Form.Item>

                  <Form.Item
                    name="lastName"
                    label="Last Name"
                    className="mb-0"
                    rules={[{ required: true, message: "Last name is required" }]}
                  >
                    <Input placeholder="Enter last name" />
                  </Form.Item>
                </div>
              </>
            )}
          </>
        )}

        <Form.Item
          name="vendorName"
          label="Vendor Name"
          rules={[{ required: true, message: "Vendor name is required" }]}
        >
          <Input placeholder="Enter vendor name" />
        </Form.Item>

        <Form.Item
          name="vendorLegalName"
          label="Vendor Legal Name"
          rules={[{ required: !isIndividual, message: "Vendor legal name is required" }]}
        >
          <Input placeholder="Enter vendor legal name" />
        </Form.Item>

        {isNetSuiteCompany && (
          <>
            <Form.Item
              name="email"
              label="Vendor Email (Optional)"
              rules={[{ type: "email", message: "Enter a valid email" }]}
            >
              <Input placeholder="vendor@example.com" />
            </Form.Item>

            <Form.Item name="phone" label="Phone" rules={[{ validator: phoneValidator }]}>
              <Input placeholder="+91 XXXXX XXXXX" />
            </Form.Item>
          </>
        )}

        {isNetSuiteCompany && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item name="category" label="Industry" className="mb-0" rules={[{ required: true, message: "Please select an industry" }]}>
                <Select
                  placeholder="Select vendor industry"
                  showSearch
                  loading={isIndustryLoading}
                  options={(industryListData || []).map((name) => ({ label: name, value: name }))}
                />
              </Form.Item>

              <Form.Item name="currency" label="Currency" className="mb-0" rules={[{ required: true, message: "Currency is required" }]}>
                <Select
                  placeholder="Select currency"
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  loading={isCurrencyLoading}
                  notFoundContent={isCurrencyLoading ? <Spin size="small" /> : "No currencies found"}
                  options={(netsuiteDropdowns?.currency || []).map((c) => ({
                    label: `${c.name}${c.symbol ? ` (${c.symbol})` : ""}`,
                    value: c.id,
                  }))}
                />
              </Form.Item>
            </div>
          </>
        )}

        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="teamsId" label="Microsoft Teams ID" className="mb-0">
            <Input placeholder="Enter Teams ID" />
          </Form.Item>

          <Form.Item name="skypeId" label="Skype ID" className="mb-0">
            <Input placeholder="Enter Skype ID" />
          </Form.Item>
        </div> */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Form.Item
            name="domain"
            label="Domain"
            rules={[{ required: true, message: "Domain is required" }, { validator: urlValidator }]}
          >
            <Input placeholder="example.com" />
          </Form.Item>

          <Form.Item
            name="linkedinUrl"
            label="LinkedIn URL"
            className="mb-0"
            rules={[{ validator: urlValidator }]}
          >
            <Input placeholder="https://linkedin.com/company/example" />
          </Form.Item>

          {/* <Form.Item name="otherIntegrationId" label="Other Integration ID" className="mb-0">
            <Input placeholder="Enter other integration ID" />
          </Form.Item> */}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="country" label="Country" className="mb-0" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder="Select country"
              onChange={(value) => {
                setSelectedCountry(value);
                form.setFieldsValue({ state: undefined });
                if (value && COUNTRY_TIMEZONES[value as AllowedCountry]) {
                  form.setFieldsValue({ timezone: COUNTRY_TIMEZONES[value as AllowedCountry][0] });
                } else {
                  form.setFieldsValue({ timezone: undefined });
                }
              }}
              options={(isNetSuiteCompany ? NETSUITE_COUNTRIES.map((c) => c.name) : ALLOWED_COUNTRIES).map((country) => ({
                label: country,
                value: country,
              }))}
            />
          </Form.Item>

          <Form.Item name="timezone" label="Timezone" className="mb-0">
            {selectedCountry && COUNTRY_TIMEZONES[selectedCountry as AllowedCountry] ? (
              <Select
                showSearch
                placeholder="Select timezone"
                options={COUNTRY_TIMEZONES[selectedCountry as AllowedCountry].map((tz) => ({
                  label: tz,
                  value: tz,
                }))}
              />
            ) : (
              <Input placeholder={selectedCountry ? "Enter timezone" : "Select country first"} disabled={!selectedCountry} />
            )}
          </Form.Item>
        </div>

        {isNetSuiteCompany ? (
          <>
            <Form.Item name="addressLine1" label="Address Line 1" rules={[{ required: true, message: "Address Line 1 is required" }]}>
              <Input maxLength={150} placeholder="Enter address line 1" />
            </Form.Item>

            <Form.Item name="addressLine2" label="Address Line 2" rules={[{ required: true, message: "Address Line 2 is required" }]}>
              <Input maxLength={150} placeholder="Enter address line 2" />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item name="city" label="City" className="mb-0" rules={[{ required: true, message: "City is required" }]}>
                <Input placeholder="Enter city" />
              </Form.Item>

              <Form.Item name="state" label="State" className="mb-0" rules={[{ required: true, message: "State is required" }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={selectedCountry ? "Select state" : "Select a country first"}
                  disabled={!selectedCountry}
                  loading={isStatesLoading}
                  notFoundContent={isStatesLoading ? <Spin size="small" /> : "No states found"}
                  options={(netsuiteStates || []).map((s) => ({
                    label: s.shortname ? `${s.name} (${s.shortname})` : s.name,
                    value: s.shortname || s.name,
                  }))}
                />
              </Form.Item>
            </div>

            <Form.Item name="pin" label="Pin" {...pinItemProps}>
              <Input placeholder="Enter pin/postal code" />
            </Form.Item>
          </>
        ) : (
          <Form.Item name="address" label="Address" rules={[{ required: true, message: "Address is required" }]}>
            <Input.TextArea rows={2} placeholder="Enter full address" />
          </Form.Item>
        )}

        <div className="mb-4">
          <p className="font-semibold text-gray-800 mb-3 text-base">Documents</p>
          {documentMode === "other" ? (
            <CustomDocumentUploadList
              form={form}
              fieldName="customDocuments"
              value={customDocuments}
              onChange={setCustomDocuments}
              onPreviewExisting={(docId) => vendor?.id && openDocumentFromApi(() => vendorService.getVendorCustomDocumentUrl(vendor.id!, docId))}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(documentMode === "us" ? US_DOCUMENTS : INDIA_DOCUMENTS).map((doc) => (
                <div key={doc.key}>
                  <ClientDocumentUploadField
                    form={form}
                    fieldName={doc.fieldName}
                    label={doc.label}
                    required={doc.required}
                    fileList={documentFiles[doc.key]}
                    onChange={(fl) => setDocumentFiles((prev) => ({ ...prev, [doc.key]: fl }))}
                    onPreviewExisting={() => vendor?.id && openDocumentFromApi(() => vendorService.getVendorDocumentUrl(vendor.id!, doc.key))}
                  />
                  {doc.numberField && (
                    <Form.Item
                      name={doc.numberField.name}
                      label={doc.numberField.label}
                      rules={[
                        { required: documentMode === "india", message: `${doc.numberField.label} is required` },
                        { validator: doc.numberField.validator },
                      ]}
                    >
                      <Input
                        placeholder={`Enter ${doc.numberField.label.toLowerCase()}`}
                        onChange={(e) => form.setFieldsValue({ [doc.numberField!.name]: e.target.value.toUpperCase() })}
                      />
                    </Form.Item>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <Form.Item name="paymentTerms" label="Payment Terms" className="mb-0" rules={[{ required: true, message: "Payment term is required" }]}>
          {isNetSuiteCompany ? (
            <Select
              showSearch
              placeholder="Select payment terms"
              loading={isPaymentTermsLoading}
              options={(paymentTermsData || []).map((term) => ({ label: term, value: term }))}
            />
          ) : (
            <Select
              showSearch
              placeholder="Select payment terms"
              options={[
                { label: "Prepaid", value: "prepaid" },
                { label: "Net 30", value: "net30" },
                { label: "Net 60", value: "net60" },
                { label: "Net 90", value: "net90" },
                { label: "Net 120", value: "net120" },
              ]}
            />
          )}
        </Form.Item>

        {!isNetSuiteCompany && (
          <>
            <Form.Item
              name="category"
              label="Industry"
              rules={[{ required: true, message: "Please select a category" }]}
            >
              <Select
                placeholder="Select vendor industry"
                showSearch
                loading={isCategoriesLoading}
                options={categoryOptions}
                onChange={(value) => setIsOtherCategory(value === OTHER_CATEGORY)}
              />
            </Form.Item>

            {isOtherCategory && (
              <Form.Item
                name="customCategory"
                label="New Industry"
                rules={[{ required: true, message: "Please enter the new industry name" }]}
              >
                <Input placeholder="Enter new industry name" />
              </Form.Item>
            )}
          </>
        )}

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} placeholder="Additional details" />
        </Form.Item>

        <div className="md:col-span-2 mt-2 mb-2">
          <h3 className="text-lg font-semibold">Bank Details</h3>
        </div>

        <Form.Item name="bankName" label="Bank Name" rules={[{ required: documentMode === "india", message: "Bank name is required" }]}>
          <Input placeholder="Bank Name" />
        </Form.Item>

        <Form.Item name="accountNumber" label="Account Number" rules={[{ required: documentMode === "india", message: "Account number is required" }]}>
          <Input placeholder="Account Number" />
        </Form.Item>

        <Form.Item name="accountHolderName" label="Account Holder Name" rules={[{ required: documentMode === "india", message: "Account holder name is required" }]}>
          <Input placeholder="Account Holder Name" />
        </Form.Item>

        {documentMode === "india" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="ifscCode"
              label="IFSC Code"
              className="mb-0"
              rules={[
                { required: true, message: "IFSC code is required" },
                { pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: "Invalid IFSC format (e.g. HDFC0001234)" },
              ]}
            >
              <Input
                placeholder="Enter IFSC code"
                onChange={(e) => form.setFieldsValue({ ifscCode: e.target.value.toUpperCase() })}
              />
            </Form.Item>

            <Form.Item name="accountType" label="Account Type" className="mb-0" rules={[{ required: true, message: "Account type is required" }]}>
              <Select
                placeholder="Select account type"
                options={[
                  { label: "Savings", value: "Savings" },
                  { label: "Current", value: "Current" },
                ]}
              />
            </Form.Item>
          </div>
        )}

        <Form.Item noStyle dependencies={["accountNumber"]}>
          {() => {
            const accountNumber = form.getFieldValue("accountNumber");
            if (!selectedCountry || !accountNumber) return null;
            return (
              <>
                {(COUNTRY_BANK_FIELDS[selectedCountry as AllowedCountry] || []).map((field) => (
                  <Form.Item
                    key={field.key}
                    name={["bankMetadata", field.key]}
                    label={field.label}
                    rules={field.required ? [{ required: true, message: `${field.label} is required` }] : []}
                  >
                    <Input placeholder={field.label} />
                  </Form.Item>
                ))}
              </>
            );
          }}
        </Form.Item>

        <Form.Item name="bankDetailNotes" label="Bank Detail Notes">
          <Input.TextArea rows={2} placeholder="Enter any additional bank detail notes" />
        </Form.Item>

        <div className="flex justify-end gap-3">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isSaving}>
            {isEditMode ? "Save Changes" : "Create Vendor"}
          </Button>
        </div>
      </Form>

      <DuplicateSelectionModal
        open={showDuplicateModal}
        type="vendor"
        items={foundDuplicates}
        onSelect={handleSelectDuplicate}
        onCancel={() => {
          setShowDuplicateModal(false);
          setPendingValues(null);
        }}
        onProceedAnyway={() => {
          if (pendingValues) {
            proceedWithCreation(pendingValues);
          }
        }}
        isSaving={isSaving}
      />
    </Modal>
  );
}
